import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Tooltip, styled } from '@material-ui/core';

import { flip, shift } from '@floating-ui/dom';
import { RiArrowRightSLine } from '@remixicon/react';
import { Editor, Extension, Range } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import suggestion, {
  SuggestionKeyDownProps,
  type SuggestionOptions,
} from '@tiptap/suggestion';
import classNames from 'classnames';

import { FloatingCard } from './FloatingCard';
import { BlockTypes } from './blockTypes';
import { positionFloatingElement } from './useFloatingPosition';

const SUBMENU_CLOSE_DELAY = 150;

type Command = {
  title: string;
  icon?: ReactNode;
  nodeType?: string;
  section?: string;
  command?: SuggestionOptions['command'];
  submenu?: Command[];
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
};

type CommandListProps = {
  items: Command[];
  command: (item: Command) => void;
  editor?: Editor;
  range?: Range;
};

type CommandListRef = {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
};

const Menu = styled('ul')({
  listStyle: 'none',
  minWidth: '200px',
  margin: 0,
  padding: 0,
  maxHeight: '315px',
  overflowY: 'auto',
});

const MenuItem = styled('li')({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 'var(--encore-text-size-smaller-2)',
  gap: 'var(--encore-spacing-tighter)',
  padding: 'var(--encore-spacing-tighter-2)',

  '&:hover, &.selected, &:focus': {
    background: 'var(--background-tinted-highlight)',
  },
  '&:active': {
    background: 'var(--background-tinted-press)',
  },
  '&.empty, &.disabled': {
    color: 'var(--essential-subdued)',
    cursor: 'default',
  },
  '&.disabled:hover, &.disabled.selected, &.disabled:focus': {
    background: 'none',
  },
});

const MenuItemIcon = styled('span')({
  alignItems: 'center',
  display: 'flex',
  color: 'var(--text-subdued)',
  justifyContent: 'center',
});

const MenuHeading = styled('li')({
  color: 'var(--text-subdued)',
  fontSize: 'var(--encore-text-size-smaller-3)',
  letterSpacing: '0.05em',
  padding: 'var(--encore-spacing-tighter-2)',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

const SubmenuContainer = styled('div')({
  position: 'relative',
});

const SubmenuIndicator = styled('span')({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
});

const Submenu = styled('div')({
  position: 'fixed',
  minWidth: '200px',
  maxHeight: '400px',
  overflowY: 'auto',
  zIndex: 10000,
});

const SubmenuItem = styled('div')({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 'var(--encore-text-size-smaller-2)',
  padding: 'var(--encore-spacing-tighter-2)',
  whiteSpace: 'nowrap',

  '&:hover, &:focus, &.selected': {
    background: 'var(--background-tinted-highlight)',
  },
  '&:active': {
    background: 'var(--background-tinted-press)',
  },
});

const CommandList = forwardRef<CommandListRef, CommandListProps>(
  ({ items, command, editor, range }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
    const [submenuSelectedIndex, setSubmenuSelectedIndex] = useState(0);
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const menuItemRefs = useRef<Map<number, HTMLElement>>(new Map());
    const submenuItemRefs = useRef<Map<number, HTMLElement>>(new Map());
    const submenuAnchorRef = useRef<Element | null>(null);

    const clearCloseTimeout = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }, []);

    useEffect(() => {
      setSelectedIndex(0);
      setOpenSubmenu(null);
      setSubmenuSelectedIndex(0);
      return clearCloseTimeout;
    }, [items, clearCloseTimeout]);

    // Scroll selected item into view
    useEffect(() => {
      const element = menuItemRefs.current.get(selectedIndex);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, [selectedIndex]);

    // Scroll selected submenu item into view
    useEffect(() => {
      const element = submenuItemRefs.current.get(submenuSelectedIndex);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, [submenuSelectedIndex]);

    const updateSubmenuPosition = useCallback((element: Element) => {
      const rect = element.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 8, // 8px spacing
      });
    }, []);

    const handleSubmenuOpen = useCallback(
      (title: string, element: Element) => {
        clearCloseTimeout();
        submenuAnchorRef.current = element;
        updateSubmenuPosition(element);
        setOpenSubmenu(title);
        setSubmenuSelectedIndex(0);
      },
      [clearCloseTimeout, updateSubmenuPosition],
    );

    const handleSubmenuClose = useCallback(() => {
      closeTimeoutRef.current = setTimeout(() => {
        setOpenSubmenu(null);
        setSubmenuSelectedIndex(0);
        submenuAnchorRef.current = null;
      }, SUBMENU_CLOSE_DELAY);
    }, []);

    // Update submenu position on scroll
    useEffect(() => {
      if (!openSubmenu || !submenuAnchorRef.current) return undefined;

      const updatePosition = () => {
        if (submenuAnchorRef.current) {
          updateSubmenuPosition(submenuAnchorRef.current);
        }
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [openSubmenu, updateSubmenuPosition]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item?.command && !item.disabled) {
          command(item);
        }
      },
      [items, command],
    );

    const selectSubmenuItem = useCallback(
      (subitem: Command) => {
        if (subitem.command && editor && range) {
          subitem.command({
            editor,
            range,
            props: subitem,
          });
        }
      },
      [editor, range],
    );

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown({ event }: SuggestionKeyDownProps) {
          const selectedItem = items[selectedIndex];
          const hasSubmenu =
            selectedItem?.submenu && selectedItem.submenu.length > 0;
          const isSubmenuOpen = openSubmenu === selectedItem?.title;

          // Handle submenu navigation when submenu is open
          if (isSubmenuOpen && hasSubmenu) {
            const submenuItems = selectedItem.submenu!;
            switch (event.key) {
              case 'ArrowUp':
                setSubmenuSelectedIndex(
                  prev =>
                    (prev + submenuItems.length - 1) % submenuItems.length,
                );
                return true;
              case 'ArrowDown':
                setSubmenuSelectedIndex(
                  prev => (prev + 1) % submenuItems.length,
                );
                return true;
              case 'ArrowLeft':
              case 'Escape':
                setOpenSubmenu(null);
                setSubmenuSelectedIndex(0);
                return true;
              case 'Enter':
                selectSubmenuItem(submenuItems[submenuSelectedIndex]);
                return true;
              default:
                return false;
            }
          }

          // Handle main menu navigation
          switch (event.key) {
            case 'ArrowUp':
              setSelectedIndex(
                prev => (prev + items.length - 1) % items.length,
              );
              return true;
            case 'ArrowDown':
              setSelectedIndex(prev => (prev + 1) % items.length);
              return true;
            case 'ArrowRight':
            case 'Enter':
              if (selectedItem?.disabled) {
                return true;
              }
              // If item has submenu, open it, otherwise select the item
              if (hasSubmenu) {
                const element = menuItemRefs.current.get(selectedIndex);
                if (element) {
                  handleSubmenuOpen(selectedItem.title, element);
                }
                return true;
              }
              selectItem(selectedIndex);
              return true;
            default:
              return false;
          }
        },
      }),
      [
        items,
        selectedIndex,
        selectItem,
        openSubmenu,
        submenuSelectedIndex,
        selectSubmenuItem,
        handleSubmenuOpen,
      ],
    );

    const setMenuItemRef = useCallback(
      (index: number, element: HTMLElement | null) => {
        if (element) {
          menuItemRefs.current.set(index, element);
        } else {
          menuItemRefs.current.delete(index);
        }
      },
      [],
    );

    const setSubmenuItemRef = useCallback(
      (index: number, element: HTMLElement | null) => {
        if (element) {
          submenuItemRefs.current.set(index, element);
        } else {
          submenuItemRefs.current.delete(index);
        }
      },
      [],
    );

    // Group items by section and flatten with headings
    const renderItems = useMemo(() => {
      const sections = items.reduce(
        (acc, item) => {
          const section = item.section || 'Basic blocks';
          if (!acc[section]) {
            acc[section] = [];
          }
          acc[section].push(item);
          return acc;
        },
        {} as Record<string, Command[]>,
      );

      const flattened: Array<
        { type: 'heading'; title: string } | { type: 'item'; item: Command }
      > = [];

      Object.entries(sections).forEach(([sectionTitle, sectionItems]) => {
        flattened.push({ type: 'heading', title: sectionTitle });
        sectionItems.forEach(item => {
          flattened.push({ type: 'item', item });
        });
      });

      return flattened;
    }, [items]);

    return (
      <FloatingCard>
        <Menu role="menu" aria-label="Block menu">
          {items.length > 0 ? (
            renderItems.map((entry, index) => {
              if (entry.type === 'heading') {
                return (
                  <MenuHeading key={`heading-${index}`}>
                    {entry.title}
                  </MenuHeading>
                );
              }
              const itemIndex = items.findIndex(i => i === entry.item);
              const hasSubmenu =
                entry.item.submenu && entry.item.submenu.length > 0;
              const isSubmenuOpen = openSubmenu === entry.item.title;

              if (hasSubmenu) {
                return (
                  <SubmenuContainer
                    key={index}
                    ref={el => setMenuItemRef(itemIndex, el)}
                    onMouseEnter={e =>
                      handleSubmenuOpen(entry.item.title, e.currentTarget)
                    }
                    onMouseLeave={handleSubmenuClose}
                  >
                    <MenuItem
                      role="menuitem"
                      tabIndex={0}
                      className={itemIndex === selectedIndex ? 'selected' : ''}
                    >
                      {entry.item.icon && (
                        <MenuItemIcon>{entry.item.icon}</MenuItemIcon>
                      )}
                      {entry.item.title}
                      <SubmenuIndicator>
                        <RiArrowRightSLine size={16} />
                      </SubmenuIndicator>
                    </MenuItem>
                    {isSubmenuOpen && (
                      <Submenu
                        style={{
                          top: submenuPosition.top,
                          left: submenuPosition.left,
                        }}
                        onMouseEnter={clearCloseTimeout}
                        onMouseLeave={handleSubmenuClose}
                      >
                        <FloatingCard>
                          <Menu role="menu">
                            {entry.item.submenu!.map((subitem, subindex) => (
                              <SubmenuItem
                                key={subindex}
                                ref={el => setSubmenuItemRef(subindex, el)}
                                role="menuitem"
                                tabIndex={0}
                                className={classNames({
                                  selected: subindex === submenuSelectedIndex,
                                })}
                                onMouseEnter={() =>
                                  setSubmenuSelectedIndex(subindex)
                                }
                                onClick={() => {
                                  if (subitem.command && editor && range) {
                                    subitem.command({
                                      editor,
                                      range,
                                      props: subitem,
                                    });
                                  }
                                }}
                              >
                                {subitem.title}
                              </SubmenuItem>
                            ))}
                          </Menu>
                        </FloatingCard>
                      </Submenu>
                    )}
                  </SubmenuContainer>
                );
              }

              const menuItem = (
                <MenuItem
                  key={index}
                  ref={el => setMenuItemRef(itemIndex, el)}
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => selectItem(itemIndex)}
                  className={classNames({
                    selected: itemIndex === selectedIndex,
                    disabled: entry.item.disabled,
                  })}
                  aria-disabled={entry.item.disabled || undefined}
                >
                  {entry.item.icon && (
                    <MenuItemIcon>{entry.item.icon}</MenuItemIcon>
                  )}
                  {entry.item.title}
                </MenuItem>
              );

              if (entry.item.disabledReason) {
                return (
                  <Tooltip
                    key={index}
                    title={entry.item.disabledReason}
                    placement="top"
                  >
                    {menuItem}
                  </Tooltip>
                );
              }

              return menuItem;
            })
          ) : (
            <MenuItem role="menuitem" tabIndex={0} className="empty">
              No commands found
            </MenuItem>
          )}
        </Menu>
      </FloatingCard>
    );
  },
);

CommandList.displayName = 'CommandList';

type ExtractNotUndefined<T> = T extends infer S | undefined ? S : T;
type RenderFunction = ExtractNotUndefined<
  Required<SuggestionOptions<Command>>['render']
>;

const renderItems = (): ReturnType<RenderFunction> => {
  let component: ReactRenderer<CommandListRef> | null = null;
  let popup: HTMLElement | null = null;
  let currentClientRect: (() => DOMRect | null) | null = null;
  let scrollListener: (() => void) | null = null;
  let resizeListener: (() => void) | null = null;

  const updatePosition = async (rect: DOMRect) => {
    if (!popup) return;

    const virtualElement = {
      getBoundingClientRect: () => rect,
    };

    await positionFloatingElement({
      anchorElement: virtualElement,
      floatingElement: popup,
      middleware: [flip(), shift({ padding: 8 })],
    });
  };

  const setupScrollListeners = () => {
    scrollListener = () => {
      if (currentClientRect) {
        const rect = currentClientRect();
        if (rect) {
          updatePosition(rect);
        }
      }
    };

    resizeListener = () => {
      if (currentClientRect) {
        const rect = currentClientRect();
        if (rect) {
          updatePosition(rect);
        }
      }
    };

    window.addEventListener('scroll', scrollListener, true);
    window.addEventListener('resize', resizeListener);
  };

  const cleanupScrollListeners = () => {
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener, true);
    }
    if (resizeListener) {
      window.removeEventListener('resize', resizeListener);
    }
    scrollListener = null;
    resizeListener = null;
  };

  return {
    onStart: props => {
      component = new ReactRenderer(CommandList, {
        props: { ...props, editor: props.editor, range: props.range },
        editor: props.editor,
      }) as ReactRenderer<CommandListRef>;

      popup = document.createElement('div');
      popup.style.position = 'absolute';
      popup.style.zIndex = '9999';
      popup.appendChild(component.element);
      document.body.appendChild(popup);

      currentClientRect = props.clientRect ?? null;
      updatePosition(props.clientRect?.() ?? new DOMRect());
      setupScrollListeners();
    },

    onUpdate(props) {
      component?.updateProps({
        ...props,
        editor: props.editor,
        range: props.range,
      });
      currentClientRect = props.clientRect ?? null;
      updatePosition(props.clientRect?.() ?? new DOMRect());
    },

    onKeyDown(props) {
      if (props.event.key === 'Escape') {
        cleanupScrollListeners();
        popup?.remove();

        return true;
      }

      return component?.ref?.onKeyDown(props) ?? false;
    },

    onExit() {
      cleanupScrollListeners();
      popup?.remove();
      component?.destroy();
    },
  };
};

export type BlockMenuOptions = {
  commands: Command[] | ((editor: Editor) => Command[]);
};

const isNodeTypeAllowedInParent = (
  editor: Editor,
  nodeTypeName: string,
): boolean => {
  const { $from } = editor.state.selection;
  const nodeType = editor.schema.nodes[nodeTypeName];

  if (!nodeType) {
    return true;
  }

  // Find the parent block that will contain the new node
  // We go up from the current position to find the first block-level container
  // that has explicit content rules (not just 'block+')
  for (let depth = $from.depth - 1; depth >= 0; depth--) {
    const parent = $from.node(depth);

    if (parent.type.name === 'doc') {
      return true;
    }

    const isAllowed = parent.type.contentMatch.matchType(nodeType) !== null;

    if (!isAllowed) {
      const paragraphType = editor.schema.nodes.paragraph;
      const parentAllowsBlocks =
        paragraphType &&
        parent.type.contentMatch.matchType(paragraphType) !== null;

      if (parentAllowsBlocks) {
        return false;
      }
      continue;
    }

    return true;
  }

  return true;
};

const commandFilter =
  (commands: Command[], editor: Editor) =>
  ({ query }: { query: string }) => {
    return commands
      .filter(item => item.title.toLowerCase().includes(query.toLowerCase()))
      .filter(item => {
        if (!item.nodeType) {
          return true;
        }
        return isNodeTypeAllowedInParent(editor, item.nodeType);
      });
  };

export const BlockMenu = Extension.create<BlockMenuOptions>({
  name: 'block-menu',

  addOptions() {
    return {
      commands: [],
    };
  },

  addProseMirrorPlugins() {
    const commands = this.options.commands;

    return [
      suggestion({
        char: '/',
        startOfLine: true,
        command: ({ editor, range, props }) => {
          props.command({ editor, range, props });
        },
        editor: this.editor,
        render: renderItems,
        allow: ({ state }) => {
          const { $from } = state.selection;
          const disallowedNodes: string[] = [
            BlockTypes.TAG,
            BlockTypes.CAPTION,
            BlockTypes.BULLET_LIST,
            BlockTypes.ORDERED_LIST,
            BlockTypes.LIST_ITEM,
            BlockTypes.HIGHLIGHT_BLOCK,
          ];

          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (disallowedNodes.includes(node.type.name)) {
              return false;
            }
          }
          return true;
        },
        items: ({ query }: { query: string }) => {
          // Resolve commands dynamically each time to get fresh data
          const resolvedCommands =
            typeof commands === 'function' ? commands(this.editor) : commands;
          return commandFilter(resolvedCommands, this.editor)({ query });
        },
      }),
    ];
  },
});
